/**
 * NotificationSocketService — sl-fdwa.3.1
 *
 * Socket.IO client for the /notification namespace. Receives real-time
 * notification pushes from the backend NotificationGateway.
 *
 * Scope: ServerScope (per-server session, not per-workspace).
 * Notifications are user-scoped — one connection per authenticated session.
 *
 * Auto-started via @OnEvent(ServerStarted) — connects when the server is
 * available. Disconnects on AccountChanged (logout) and reconnects on login.
 *
 * Events:
 * - notification:created → revalidate count + toast for high-level
 * - notification:count → direct count update
 * - copilot:history-changed → window event for open Copilot chat panels
 *
 * Each socket connection performs one count reconciliation. Afterwards pushed
 * count events own the live state; focus remains the missed-event fallback.
 */
import { Avatar, notify } from '@affine/component';
import { I18n } from '@affine/i18n';
import { isDebugVerbosityAtLeast } from '@affine/nbstore';
import { COPILOT_HISTORY_CHANGED_EVENT } from '@streamlain/copilot-realtime-contract';
import type { MailChangedEventV1 } from '@streamlain/mail-provider-contract';
import { OnEvent, Service } from '@toeverything/infra';
import { createElement, type KeyboardEvent, type MouseEvent } from 'react';
import type { Socket } from 'socket.io-client';

import {
  createServerSocketConnection,
  ensureSocketServerConfig,
} from '../../../utils/socket';
import { AccountChanged, type AuthService, ServerService } from '../../cloud';
import { ServerStarted } from '../../cloud/events/server-started';
import { NavigationPanelRevealService } from '../../navigation-panel';
import {
  dispatchCopilotHistoryChangedEvent,
  dispatchCopilotHistoryReconnectedEvent,
} from '../../streamlain/copilot/copilot-history-realtime-events';
import {
  dispatchMailChangedEvent,
  dispatchMailReconnectEvent,
} from '../../streamlain/mail/mail-realtime-events';
import type { Notification } from '../stores/notification';
import { NotificationType } from '../stores/notification';
import {
  formatDocNotification,
  formatFolderNotification,
} from '../utils/permission-notification-copy';
import type { NotificationCountService } from './count';
import type { NotificationListService } from './list';

/** Event payload from backend NotificationGateway */
interface NotificationCreatedEvent {
  notification: Notification;
  timestamp: number;
}

/** Event payload for count updates */
interface NotificationCountEvent {
  count: number;
  timestamp: number;
}

type NotificationActorSnapshot = {
  avatarUrl?: string | null;
  name?: string | null;
};

type NotificationWithActor = Notification & {
  createdByUser?: unknown;
};

function readNotificationActor(
  notification: NotificationCreatedEvent['notification']
): NotificationActorSnapshot | null {
  const body = notification.body ?? {};
  const nestedActor = [
    body.inviter,
    body.createdByUser,
    (notification as NotificationWithActor).createdByUser,
  ].find(candidate => candidate && typeof candidate === 'object') as
    | Record<string, unknown>
    | undefined;
  const name =
    typeof body.actorName === 'string'
      ? body.actorName
      : typeof nestedActor?.name === 'string'
        ? nestedActor.name
        : null;
  const avatarUrl =
    typeof body.actorAvatarUrl === 'string'
      ? body.actorAvatarUrl
      : typeof nestedActor?.avatarUrl === 'string'
        ? nestedActor.avatarUrl
        : null;

  return name || avatarUrl ? { avatarUrl, name } : null;
}

function renderNotificationActorAvatar(
  notification: NotificationCreatedEvent['notification'],
  size = 28
) {
  const actor = readNotificationActor(notification);
  if (!actor) return undefined;

  return createElement(Avatar, {
    'aria-label': actor.name ?? undefined,
    ...({
      'data-testid': 'notification-actor-avatar',
    } as Record<string, string>),
    colorfulFallback: true,
    name: actor.name ?? undefined,
    size,
    url: actor.avatarUrl ?? null,
  });
}

function renderPermissionNotificationMessage(
  notification: NotificationCreatedEvent['notification'],
  message: string
) {
  const actor = readNotificationActor(notification);
  if (!actor?.name) return message;

  return createElement(
    'span',
    {
      'data-testid': 'permission-notification-actor-row',
      style: {
        alignItems: 'center',
        display: 'inline-flex',
        gap: 8,
      },
    },
    renderNotificationActorAvatar(notification, 24),
    createElement(
      'span',
      null,
      createElement(
        'span',
        { 'data-testid': 'notification-actor-name' },
        actor.name
      ),
      `: ${message}`
    )
  );
}

export function formatNotificationToastPreview(
  notification: NotificationCreatedEvent['notification']
): { title: string; message: string } {
  const body = notification.body ?? {};
  const actorName = readNotificationActor(notification)?.name;
  const docTitle =
    typeof body.docTitle === 'string' && body.docTitle.trim()
      ? body.docTitle.trim()
      : null;

  if (notification.type === NotificationType.BlockInvite) {
    if (body.documentOnly === true) {
      return {
        title: `Document invitation · ${docTitle ?? I18n.Untitled()}`,
        message: `${actorName ?? 'A teammate'} invited you to this document`,
      };
    }
    const action = body.assignment
      ? 'assigned you to a block in'
      : 'invited you to a block in';
    const subject = docTitle ?? I18n.Untitled();
    const blockPreview =
      typeof body.blockPreview === 'string' && body.blockPreview.trim()
        ? body.blockPreview.trim()
        : 'Block';
    return {
      title: `Block invitation · ${blockPreview}`,
      message: `${actorName ?? 'A teammate'} ${action} ${subject}`,
    };
  }

  const explicitMessage =
    typeof body.message === 'string' && body.message.trim()
      ? body.message.trim()
      : null;
  if (explicitMessage) {
    return {
      title: docTitle ?? 'Notification',
      message: explicitMessage,
    };
  }

  const subject = [body.blockTitle, body.title, body.name, body.docTitle]
    .find(value => typeof value === 'string' && value.trim())
    ?.toString()
    .trim();
  const eventLabel = notification.type.replace(/([a-z])([A-Z])/g, '$1 $2');
  return {
    title: subject ? `${eventLabel} · ${subject}` : eventLabel,
    message: subject
      ? `${eventLabel} for ${subject}`
      : `${eventLabel} event received`,
  };
}
@OnEvent(ServerStarted, s => s.handleServerStarted)
@OnEvent(AccountChanged, s => s.handleAccountChanged)
export class NotificationSocketService extends Service {
  private socket: Socket | null = null;
  private connected = false;
  private connecting: Promise<void> | null = null;
  private connectionGeneration = 0;
  private pendingConnectionCancel: (() => void) | null = null;
  private authenticationWait:
    | {
        controller: AbortController;
        promise: Promise<void>;
      }
    | undefined;
  private readonly seenMailEventIds = new Set<string>();

  constructor(
    private readonly serverService: ServerService,
    private readonly authService: AuthService,
    private readonly countService: NotificationCountService,
    private readonly listService: NotificationListService,
    private readonly navigationPanelRevealService: NavigationPanelRevealService
  ) {
    super();
  }

  // ===========================================================================
  // Lifecycle Events
  // ===========================================================================

  handleServerStarted(): void {
    this.start();
  }

  start(): void {
    if (this.authService.session.status$.value === 'authenticated') {
      this.cancelAuthenticationWait();
      this.connectWithErrorHandling('Auto-connect');
      return;
    }

    if (this.authenticationWait) return;

    const controller = new AbortController();
    const promise = this.authService.session
      .waitForAuthenticated(controller.signal)
      .then(async () => {
        if (!controller.signal.aborted) {
          await this.connect();
        }
      })
      .catch(error => {
        if (!controller.signal.aborted) {
          console.error(
            '[NotificationSocket] Authentication wait failed:',
            error
          );
        }
      })
      .finally(() => {
        if (this.authenticationWait?.controller === controller) {
          this.authenticationWait = undefined;
        }
      });
    this.authenticationWait = { controller, promise };
  }

  handleAccountChanged(): void {
    if (this.authService.session.status$.value === 'authenticated') {
      // Re-connect with new session
      this.cancelAuthenticationWait();
      this.disconnect();
      this.connectWithErrorHandling('Reconnect');
    } else {
      // Logged out — disconnect
      this.disconnect();
      this.start();
    }
  }

  private connectWithErrorHandling(context: string): void {
    this.connect().catch(error => {
      console.error(`[NotificationSocket] ${context} failed:`, error);
    });
  }

  private cancelAuthenticationWait(): void {
    this.authenticationWait?.controller.abort();
    this.authenticationWait = undefined;
  }

  // ===========================================================================
  // Socket Connection
  // ===========================================================================

  /**
   * Connect to Socket.IO /notification namespace.
   * The backend auto-joins the user to their notification:{userId} room on auth.
   */
  async connect(): Promise<void> {
    if (this.connected) return;
    if (this.connecting) return this.connecting;
    // A socket that is still reconnecting owns this lifecycle already.
    if (this.socket) return;

    const attempt = this.connectOnce(this.connectionGeneration);
    this.connecting = attempt;
    try {
      await attempt;
    } finally {
      if (this.connecting === attempt) {
        this.connecting = null;
      }
    }
  }

  private async connectOnce(generation: number): Promise<void> {
    const server = this.serverService.server;
    if (!server) {
      console.warn('[NotificationSocket] No server available');
      return;
    }

    await ensureSocketServerConfig(server);
    if (generation !== this.connectionGeneration) return;

    const socket = createServerSocketConnection(server, '/notification');
    if (generation !== this.connectionGeneration) {
      socket.disconnect();
      return;
    }
    this.socket = socket;

    // Register payload handlers before the first connection attempt. Initial
    // connect errors must not disable Socket.IO's automatic reconnect path.
    socket.on('notification:created', this.handleNotificationCreated);
    socket.on('notification:count', this.handleCountUpdate);
    socket.on('mail:changed', this.handleMailChanged);
    socket.on(COPILOT_HISTORY_CHANGED_EVENT, this.handleCopilotHistoryChanged);

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const finish = (error?: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        if (this.pendingConnectionCancel === cancel) {
          this.pendingConnectionCancel = null;
        }
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      };
      const cancel = () => finish();
      const timeout = setTimeout(() => {
        if (generation !== this.connectionGeneration) {
          finish();
          return;
        }
        finish(new Error('Connection timeout'));
      }, 10000);
      this.pendingConnectionCancel = cancel;

      socket.on('connect', () => {
        if (
          generation !== this.connectionGeneration ||
          this.socket !== socket
        ) {
          return;
        }
        clearTimeout(timeout);
        this.connected = true;
        dispatchMailReconnectEvent();
        dispatchCopilotHistoryReconnectedEvent();
        this.countService.revalidate();
        if (isDebugVerbosityAtLeast(3)) {
          console.debug('[NotificationSocket] Connected to /notification');
        }
        finish();
      });

      socket.on('connect_error', error => {
        if (generation !== this.connectionGeneration) return;
        console.warn('[NotificationSocket] Connection attempt failed:', error);
      });
    });
  }

  /**
   * Disconnect and clean up.
   */
  disconnect(): void {
    this.connectionGeneration += 1;
    this.pendingConnectionCancel?.();
    this.pendingConnectionCancel = null;
    this.connecting = null;
    if (this.socket) {
      try {
        this.socket.off('notification:created', this.handleNotificationCreated);
        this.socket.off('notification:count', this.handleCountUpdate);
        this.socket.off('mail:changed', this.handleMailChanged);
        this.socket.off(
          COPILOT_HISTORY_CHANGED_EVENT,
          this.handleCopilotHistoryChanged
        );
        this.socket.disconnect();
      } catch (error) {
        console.warn('[NotificationSocket] Error during disconnect:', error);
      }
      this.socket = null;
    }
    this.connected = false;
  }

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  /** Handle notification:created from the authoritative socket payload. */
  private readonly handleNotificationCreated = (
    event: NotificationCreatedEvent
  ): void => {
    console.debug(
      '[NotificationSocket] notification:created',
      event.notification.type
    );

    // The backend immediately follows this event with notification:count.
    // Merge the complete pushed row instead of issuing list + count GraphQL.
    this.listService.ingest(event.notification);

    // Toast for actionable notifications. This is the single channel
    // for all notification-backed toasts (including AutomationFailed /
    // AutomationStopped) — the backend drives which events become
    // notifications, the frontend just surfaces them. The duplicate
    // toast that used to live in AutomationSocketService has been
    // removed so users only see one toast per event.
    if (
      event.notification.level === 'High' ||
      event.notification.level === 'Default'
    ) {
      this.showToast(event.notification);
    }
  };

  private readonly handleMailChanged = (event: unknown): void => {
    const candidate = event as Partial<MailChangedEventV1>;
    if (
      typeof candidate.eventId !== 'string' ||
      this.seenMailEventIds.has(candidate.eventId)
    ) {
      return;
    }
    if (!dispatchMailChangedEvent(event)) return;
    this.seenMailEventIds.add(candidate.eventId);
    if (this.seenMailEventIds.size > 10_000) {
      const oldest = this.seenMailEventIds.values().next().value;
      if (oldest) this.seenMailEventIds.delete(oldest);
    }
  };

  private readonly handleCopilotHistoryChanged = (event: unknown): void => {
    dispatchCopilotHistoryChangedEvent(event);
  };

  private showToast(
    eventNotification: NotificationCreatedEvent['notification']
  ) {
    const body = eventNotification.body ?? {};

    if (eventNotification.type === NotificationType.WorkspaceMcpGrant) {
      notify.success({
        title: 'MCP-Zugang erfolgreich gewährt',
        message:
          'Öffne die Benachrichtigung, um deine MCP-Konfiguration einmalig anzuzeigen.',
      });
      return;
    }

    if (eventNotification.type === NotificationType.WorkspaceMcpRevoked) {
      notify({
        title: 'MCP-Zugang widerrufen',
        message: 'Dein MCP-Zugang für diesen Workspace wurde entzogen.',
        style: 'normal',
        theme: 'warning',
      });
      return;
    }

    if (
      eventNotification.type === NotificationType.FolderShared ||
      eventNotification.type === NotificationType.FolderRoleRevoked
    ) {
      const title = (body.folderName as string | undefined) ?? I18n.Untitled();
      const message = formatFolderNotification(I18n, eventNotification.type, {
        ...body,
        actorName: null,
      });
      notify(
        {
          icon: null,
          title,
          message: renderPermissionNotificationMessage(
            eventNotification,
            message
          ),
          style: 'normal',
          theme: 'info',
          rootAttrs: this.permissionToastRootAttrs(eventNotification),
        },
        {
          duration: 15_000,
        }
      );
      return;
    }

    if (
      eventNotification.type === NotificationType.DocShared ||
      eventNotification.type === NotificationType.DocRoleUpdated ||
      eventNotification.type === NotificationType.DocRoleRevoked
    ) {
      const title = (body.docName as string | undefined) ?? I18n.Untitled();
      const message = formatDocNotification(I18n, eventNotification.type, {
        ...body,
        actorName: null,
      });
      notify(
        {
          icon: null,
          title,
          message: renderPermissionNotificationMessage(
            eventNotification,
            message
          ),
          style: 'normal',
          theme: 'info',
          rootAttrs: this.permissionToastRootAttrs(eventNotification),
        },
        {
          duration: 15_000,
        }
      );
      return;
    }

    const preview = formatNotificationToastPreview(eventNotification);
    notify({
      icon: renderNotificationActorAvatar(eventNotification),
      title: preview.title,
      message: preview.message,
      style: 'normal',
      theme: eventNotification.level === 'High' ? 'warning' : 'info',
    });
  }

  private permissionToastRootAttrs(
    notification: NotificationCreatedEvent['notification']
  ) {
    const body = notification.body ?? {};
    const resourceId =
      typeof body.docId === 'string'
        ? body.docId
        : typeof body.folderId === 'string'
          ? body.folderId
          : undefined;
    const activate = () => {
      const workspaceId = body.workspaceId;
      if (typeof workspaceId !== 'string') return;

      if (
        (notification.type === NotificationType.DocShared ||
          notification.type === NotificationType.DocRoleUpdated) &&
        typeof body.docId === 'string'
      ) {
        this.navigationPanelRevealService.revealDoc(body.docId, {
          source: 'notification',
          workspaceId,
        });
        return;
      }

      if (
        notification.type === NotificationType.FolderShared &&
        typeof body.folderId === 'string'
      ) {
        this.navigationPanelRevealService.revealFolder(body.folderId, {
          source: 'notification',
          workspaceId,
        });
      }
    };

    return {
      'data-notification-resource-id': resourceId,
      'data-testid': 'permission-notification-toast',
      onClick: (event: MouseEvent<HTMLDivElement>) => {
        if ((event.target as HTMLElement).closest('button')) return;
        activate();
      },
      onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        activate();
      },
      role: 'button',
      tabIndex: 0,
    };
  }

  /**
   * Handle notification:count — direct count update (no GraphQL round-trip).
   */
  private readonly handleCountUpdate = (
    event: NotificationCountEvent
  ): void => {
    console.debug('[NotificationSocket] notification:count', event.count);
    this.countService.setCount(event.count);
  };

  override dispose(): void {
    this.cancelAuthenticationWait();
    this.disconnect();
    super.dispose();
  }
}
